import { useState, useEffect, useCallback } from "react";
import { useLocalization } from "../../context/LocalizationContext";
import { useWebSocket } from "../../context/WebSocketContext";
import { useAppContext } from "../../context/AppContext";
import { useAIAvatarSession } from "../../context/AIAvatarSessionContext";
import { useCustomAvatarVoice } from "../../hooks/useCustomAvatarVoice";
import { useWakeWord } from "../../hooks/useWakeWord";
import { formatUserName } from "../../utils/helpers";
import "./VitalsCheck.css";
import Header from "../../containers/Header";
import Button from "../../components/shared/Button";
import Modal from "../../components/shared/Modal";
import { VITAL_ICONS, VITAL_ICON_MIRRORED_TYPES, WAKE_WORD_KEYWORDS_EN, WAKE_WORD_KEYWORDS_AR, WS_EVENTS } from "../../constants";
import { tokenStorage } from "../../utils/tokenStorage";
import micIcon from "../../assets/icons/AI/whiteMic.svg";
import BubblingAIAssistant from "../../components/BubblingAIAssistant";
import { checkVitals } from "../../apis/patient";
import { useAuth } from "../../context/AuthContext";
import VitalCard from "./VitalCard";
import VisualMediaModal from "./VisualMediaModal";
import { mapHigoEncounterToVitalsPayloads } from "../../utils/mapHigoEncounter";
import higoEncounterMock from "../../mocks/higoEncounterMock";
import leekaMeasurementsMock from "../../mocks/leekaMeasurementsMock";

const VitalsCheck = ({ userData }) => {
  const { t, isRTL } = useLocalization();
  const { subscribe } = useWebSocket();
  const {
    podData,
    measures,
    setMeasures,
    setDeviceMeasures,
    visualMedia,
    setVisualMedia,
    ecgImageUrl,
    setEcgImageUrl,
    markStepCompleted,
    aiLanguage,
  } = useAppContext();
  // A pod is "new flow" (multi-device) if it has any device enabled.
  const isMultiDevicePod = !!(podData?.higoEnabled || podData?.leekaEnabled || podData?.omronEnabled);
  const { session } = useAuth();
  const [isMeasuring, setIsMeasuring] = useState(
    !(measures && Array.isArray(measures) && measures.length > 0)
  );
  const [readingId, setReadingId] = useState(() => tokenStorage.getReadingId());
  const [isEcgModalOpen, setIsEcgModalOpen] = useState(false);
  const [openVisualMedia, setOpenVisualMedia] = useState(null);

  // Dev-only helper to preview the full results view without real devices —
  // maps the sample HIGO encounter (visual-media exams; its own numeric
  // readings are dropped by DEFAULT_VITALS_EXCLUDED_SOURCES) plus a sample
  // LEKA vitals payload (numeric cards), the same way real Vitals /
  // VisualMediaReady events are mapped, then stores them the same way.
  const handleLoadMockHigoData = useCallback(() => {
    const { measurements, visualMediaGroups } = mapHigoEncounterToVitalsPayloads(higoEncounterMock);
    const mapMeasurements = (list) =>
      list
        .map((m) => ({
          name: (m.measurementType || "").trim(),
          value: m.value?.toString?.() ?? "",
          unit: m.unit || "",
          textValue: m.textValue || "",
        }))
        .filter((m) => m.name);

    setDeviceMeasures("HIGO", mapMeasurements(measurements));
    setDeviceMeasures("LEKA", mapMeasurements(leekaMeasurementsMock));
    visualMediaGroups.forEach((group) => {
      setVisualMedia("HIGO", group.measurementType, {
        visualMeasurementId: group.visualMeasurementId,
        files: group.files,
        readingId: null,
        timestamp: group.timestamp,
      });
    });

    markStepCompleted("vitals");
    setIsMeasuring(false);
  }, [setDeviceMeasures, setVisualMedia, markStepCompleted]);




  // Localize vital names using backend measurementType key
  const getLocalizedVitalName = (measurementType) => {
    const translated = t(`measureAspects.${measurementType}`);
    // If translation is found (not returning the key itself), use it
    if (translated !== `measureAspects.${measurementType}`) {
      return translated;
    }
    // Fallback: format the raw key as readable text
    return measurementType
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Localize known enumerated result text (e.g. "Normal"); falls back to the raw text
  // for free-form backend strings that aren't in the dictionary (e.g. ECG notes).
  const getLocalizedResultText = (textValue) => {
    const translated = t(`measureResults.${textValue}`);
    return translated !== `measureResults.${textValue}` ? translated : textValue;
  };

  // Extracts and persists the reading id from any incoming reading (vitals or
  // visual media) so it can be matched against a later EcgImageReady event.
  const captureReadingId = useCallback((data) => {
    const incomingReadingId = data?.payload?.readingId ?? data?.readingId;
    if (incomingReadingId != null) {
      tokenStorage.setReadingId(incomingReadingId);
      setReadingId(incomingReadingId);
      // A fresh reading invalidates any ECG image tied to the previous one
      setEcgImageUrl(null);
    }
  }, [setEcgImageUrl]);

  // Subscribe to vitals over WebSocket. "Vitals" is shared by legacy
  // (single-source) pods AND all 3 devices on "new flow" (multi-device) pods —
  // on multi-device pods the payload's "source" field (HIGO/LEKA/OMRON) tells
  // them apart, so only that device's slice gets replaced. Non-numeric
  // readings (images/audio) arrive on the separate "VisualMediaReady" event.
  useEffect(() => {
    const handleVitalsEvent = (data) => {
      console.log("Vitals event received:", data);
      captureReadingId(data);

      const measurements = data?.payload?.measurements || data?.measurements || [];
      const mappedMeasures = measurements
        .map((m) => {
          const rawName = (m.measurementType || m.name || "").trim();
          return {
            name: rawName,
            value: m.value?.toString?.() ?? "",
            unit: m.unit || "",
            textValue: m.textValue || "",
          };
        })
        .filter((m) => m.name);

      const source = data?.payload?.source ?? data?.source;
      if (isMultiDevicePod && source) {
        setDeviceMeasures(source, mappedMeasures);
      } else {
        setMeasures(mappedMeasures);
      }

      markStepCompleted("vitals");
      setIsMeasuring(false);
    };

    const unsubscribeVitals = subscribe("Vitals", handleVitalsEvent);

    if (!isMultiDevicePod) {
      return () => unsubscribeVitals();
    }

    const handleVisualMediaEvent = (data) => {
      console.log("VisualMediaReady event received:", data);
      captureReadingId(data);

      const payload = data?.payload ?? data;
      const { source, measurementType, visualMeasurementId, files, readingId: incomingReadingId, timestamp } = payload || {};
      if (source && measurementType) {
        setVisualMedia(source, measurementType, {
          visualMeasurementId,
          files: files || [],
          readingId: incomingReadingId,
          timestamp,
        });
      }

      markStepCompleted("vitals");
      setIsMeasuring(false);
    };

    const unsubscribeVisualMedia = subscribe(WS_EVENTS.VISUAL_MEDIA_READY, handleVisualMediaEvent);

    return () => {
      unsubscribeVitals();
      unsubscribeVisualMedia();
    };
  }, [subscribe, isMultiDevicePod, captureReadingId, setMeasures, setDeviceMeasures, setVisualMedia, markStepCompleted]);

  // Subscribe to EcgImageReady only once vitals (and their readingId) have been received.
  // The pod session (sessionId/podIp) is already validated by the subscribe() wrapper itself.
  useEffect(() => {
    if (!readingId) return;

    const handleEcgImageReady = (data) => {
      const incomingReadingId = data?.payload?.readingId ?? data?.readingId;
      const storedReadingId = tokenStorage.getReadingId();

      if (storedReadingId && incomingReadingId != null && String(incomingReadingId) !== String(storedReadingId)) {
        console.warn("EcgImageReady event received for a different reading, ignoring:", {
          incomingReadingId,
          storedReadingId,
        });
        return;
      }

      console.log("EcgImageReady event received:", data);
      setEcgImageUrl(data?.payload?.imageUrl ?? data?.imageUrl ?? null);
    };

    const unsubscribe = subscribe(WS_EVENTS.ECG_IMAGE_READY, handleEcgImageReady);

    return () => {
      unsubscribe();
    };
  }, [subscribe, readingId]);

  // Check measures in store on mount
  useEffect(() => {
    console.log("measures from store", measures);
    if (measures && Array.isArray(measures) && measures.length > 0) {
      // Measures exist in store, don't start measuring
      setIsMeasuring(false);
    } else {
      // No measures in store, start loading
      setIsMeasuring(true);
    }
  }, [measures]);
  useEffect(() => {
    setInstructionsEnabled(true);
  }, []);

  const userName = formatUserName(userData?.name ?? "");

  const {
    audioRef,
    videoRef,
    instructionVideoSrc,
    sessionToken,
    sessionId,
    isConnected,
    transcription,
    avatarSpeaking,
    repeat,
    initializeSession,
    isAgentConnecting,
    isVideoReady,
    setIsAgentConnecting,
    agentError,
    setAgentError,
    clearInstructionVideoSrc,
    setInstructionsEnabled,
    claimWakeWord,
    releaseWakeWord,
  } = useAIAvatarSession();

  const { isRecording, startRecording, stopRecording } = useCustomAvatarVoice({
    sessionId,
    repeat,
    language: aiLanguage,
  });

  const handleStopRecording = useCallback(() => {
    stopRecording();
  }, [stopRecording]);

  // Release the wake-word claim whenever recording ends, regardless of whether
  // it was stopped manually or automatically by the silence timeout.
  useEffect(() => {
    if (!isRecording) {
      releaseWakeWord();
    }
  }, [isRecording, releaseWakeWord]);

  useWakeWord({
    keywords: aiLanguage === "ar" ? WAKE_WORD_KEYWORDS_AR : WAKE_WORD_KEYWORDS_EN,
    lang: aiLanguage === "ar" ? "ar-SA" : "en-US",
    enabled: isConnected && !isRecording && !avatarSpeaking,
    onKeywordDetected: () => {
      if (!claimWakeWord()) return;
      startRecording();
    },
  });

  const handleStartSpeaking = useCallback(async () => {
    // First time: initialize session
    if (!sessionToken && !isAgentConnecting) {
      try {
        await initializeSession();
      } catch {
        setAgentError(t("liveAvatar.failedToConnect"));
      }
      return;
    }

    // Toggle recording once session is connected
    if (isConnected) {
      if (isRecording) {
        handleStopRecording();
      } else {
        await startRecording();
      }
    }
  }, [
    sessionToken,
    isConnected,
    isRecording,
    startRecording,
    handleStopRecording,
    initializeSession,
    isAgentConnecting,
    setAgentError,
    t,
  ]);

  const handleStartLeeka = () => {
    checkVitals(session?.sessionId).then((data) => {
    }).catch((err) => {
      console.error('Error checking vitals:', err);
    });
  }


  // Automatically initialize shared AI agent session when on measuring phase (same as Dashboard on load)
  useEffect(() => {
    if (!isMeasuring) return;
    if (!sessionToken && !isAgentConnecting) {
      initializeSession().catch((err) => {
        console.error("Avatar init error:", err);
        setAgentError(t("liveAvatar.failedToConnect"));
      });
    }
  }, [isMeasuring, sessionToken, isAgentConnecting, initializeSession, setAgentError, t]);

  useEffect(() => {
    handleStartLeeka();
  }, [])

  return (
    <div className="page-container scrollable-page">
      <Header
        statusText={`${t("loggedInAs")} ${userName}`}
        title={t(isMeasuring ? "vitalsCheck.checkYourVitals" : "vitalsCheck.results")}
        showSettingsButton={true}
        showHomeButton={true}
      />
      {isMeasuring ? (
        <>
          <div className="dashboard-avatar-section">
            <div className="dashboard-avatar-frame">
              {avatarSpeaking && <div className="dashboard-avatar-ring" />}
              <div className="dashboard-avatar-inner">
                {/* {!isAgentConnecting && !agentError && !instructionVideoSrc && (
                  <div className="vitals-avatar-placeholder" />
                )} */}
                {isAgentConnecting && (
                  <div className="dashboard-avatar-loading">
                    <span>{t("dashboard.connectingToAgent")}</span>
                  </div>
                )}
                {agentError && (
                  <div className="dashboard-avatar-error">
                    <span>{agentError}</span>
                  </div>
                )}
                {
                  instructionVideoSrc ? (
                    <video
                      key="instruction-video"
                      src={instructionVideoSrc}
                      className="vitals-instruction-video"
                      playsInline
                      autoPlay
                      muted={false}
                      style={{ display: "block", zIndex: 3000 }}
                      aria-label={t("settings.aiAssistant")}
                      onEnded={() => {
                        clearInstructionVideoSrc();
                        startRecording();
                      }}
                    />
                  ) :
                    (
                      // <audio
                      //   ref={audioRef}
                      //   autoPlay
                      //   muted={false}
                      //   aria-label={t("settings.aiAssistant")}
                      // />
                      <video
                        key="avatar-video"
                        ref={videoRef}
                        className="vitals-instruction-video"
                        playsInline
                        autoPlay
                        muted={false}
                        style={{ display: isVideoReady ? "block" : "none" }}
                        aria-label={t("settings.aiAssistant")}
                      />
                    )
                }
              </div>
            </div>
          </div>

          <p className="dashboard-instruction page-text">
            {transcription || t("vitalsCheck.sessionInstruction", { name: userName })}
          </p>

          {process.env.REACT_APP_ENVIRONMENT !== "production" && (
            <Button variant="secondary" onClick={() => setIsMeasuring(false)}>
              View Results
            </Button>
          )}

          <Button
            variant="secondary"
            icon={micIcon}
            iconPosition="start"
            className={`bubbling-ai-modal-start-speaking${isRecording ? " bubbling-ai-modal-start-speaking--listening" : ""}`}
            onClick={handleStartSpeaking}
            disabled={isAgentConnecting}
            iconStyle={
              isRTL
                ? { transform: "scaleX(-1)", marginLeft: "1vw", marginRight: 0 }
                : { marginRight: "1vw" }
            }
          >
            {isAgentConnecting
              ? t("dashboard.connectingToAgent")
              : isRecording
                ? t("dashboard.listening")
                : t("dashboard.startSpeaking")}
          </Button>
        </>
      ) : (
        <>
          {/* Results phase (background removed) */}
          <div className="results-container">
            {process.env.REACT_APP_ENVIRONMENT !== "production" && (
              <Button variant="secondary" onClick={handleLoadMockHigoData}>
                Load Mock Data
              </Button>
            )}
            <div className="vitals-grid">
              {(() => {
                const allMeasures = (measures && Array.isArray(measures)) ? measures : [];
                const allVisualMedia = (visualMedia && Array.isArray(visualMedia)) ? visualMedia : [];

                const measureCards = allMeasures.map((vital, index) => {
                  const isEcg = vital.name === "ECG";
                  const icon = VITAL_ICONS[vital.name];
                  const name = getLocalizedVitalName(vital.name);

                  return (
                    <VitalCard
                      key={`measure-${index}`}
                      vital={vital}
                      icon={icon}
                      name={name}
                      hasDetail={isEcg}
                      mirrorIcon={VITAL_ICON_MIRRORED_TYPES.includes(vital.name)}
                      getLocalizedResultText={getLocalizedResultText}
                      onClick={() => setIsEcgModalOpen(true)}
                    />
                  );
                });

                const visualMediaCards = allVisualMedia.map((entry) => {
                  const icon = VITAL_ICONS[entry.name];
                  const name = getLocalizedVitalName(entry.name);

                  return (
                    <VitalCard
                      key={`visual-media-${entry.name}`}
                      vital={{}}
                      icon={icon}
                      name={name}
                      hasDetail
                      mirrorIcon={VITAL_ICON_MIRRORED_TYPES.includes(entry.name)}
                      getLocalizedResultText={getLocalizedResultText}
                      onClick={() => setOpenVisualMedia(entry)}
                    />
                  );
                });

                return [...measureCards, ...visualMediaCards];
              })()}
            </div>
          </div>
          <BubblingAIAssistant />

          <Modal
            isOpen={isEcgModalOpen}
            onClose={() => setIsEcgModalOpen(false)}
            customLayout
            noScroll
            title={null}
            message={null}
          >
            <div className="ecg-modal-body">
              <h2 className="ecg-modal-title">{t("vitalsCheck.ecgTitle")}</h2>
              <div className="ecg-body">
                {ecgImageUrl ? (
                  <img src={ecgImageUrl} alt={t("vitalsCheck.ecgTitle")} className="ecg-image" />
                ) : (
                  <div className="ecg-processing">{t("vitalsCheck.ecgProcessing")}</div>
                )}
              </div>
            </div>
          </Modal>

          <VisualMediaModal
            isOpen={!!openVisualMedia}
            onClose={() => setOpenVisualMedia(null)}
            name={openVisualMedia ? getLocalizedVitalName(openVisualMedia.name) : ""}
            files={openVisualMedia?.media?.files || []}
          />
        </>
      )}
    </div>
  );
};

export default VitalsCheck;
